/**
 * Tax Configuration Index
 * 
 * Central registry for tax bracket data by year.
 * To add a new tax year:
 *   1. Create a new file: years/YYYY.ts
 *   2. Import and register it in the TAX_YEARS_REGISTRY below
 */

import { FederalTaxConfig, FilingStatus, TaxBracket } from '../types.js';
import { federal2024 } from './years/2024.js';
import { federal2025 } from './years/2025.js';
import { federal2026 } from './years/2026.js';
import {
  STATE_CODES,
  NO_INCOME_TAX_STATES,
  getStateName,
  getStateCode,
  getStateTaxConfig,
  getStateTaxRate,
  hasStateIncomeTax,
  getStateStandardDeduction,
  getStateTaxConfigsForYear,
  getAvailableStateTaxYears,
  states2024,
  states2025,
  states2026
} from './states.js';
import { assertDefined } from '../../utils/assertDefined.js';

// ============================================================================
// Tax Year Registry
// ============================================================================

/**
 * Registry of federal tax configurations by year.
 * Add new years here as they become available.
 */
const TAX_YEARS_REGISTRY: Record<number, FederalTaxConfig> = {
  2024: federal2024,
  2025: federal2025,
  2026: federal2026
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Get the default tax year (current calendar year)
 */
export function getCurrentTaxYear(): number {
  return new Date().getFullYear();
}

/**
 * Get all available tax years
 */
export function getAvailableTaxYears(): number[] {
  return Object.keys(TAX_YEARS_REGISTRY)
    .map(Number)
    .sort((a, b) => b - a); // Most recent first
}

/**
 * Get the most recent available tax year
 */
export function getMostRecentTaxYear(): number {
  const years = getAvailableTaxYears();
  return years[0] || getCurrentTaxYear();
}

/**
 * Resolve a requested tax year to the nearest year with registered
 * configuration data.
 *
 * A year older than anything registered resolves to the oldest available
 * year (the closest approximation) rather than always falling back to the
 * newest — a request for 2023 previously silently got 2026's brackets,
 * deductions, and wage base with no signal that the parameters didn't match
 * the requested year.
 */
export function resolveTaxYear(year?: number): { resolvedYear: number; isExact: boolean } {
  const targetYear = year ?? getCurrentTaxYear();

  if (TAX_YEARS_REGISTRY[targetYear]) {
    return { resolvedYear: targetYear, isExact: true };
  }

  const years = getAvailableTaxYears(); // sorted most-recent first
  if (years.length === 0) {
    return { resolvedYear: targetYear, isExact: false };
  }

  const newest = years[0];
  const oldest = years[years.length - 1];
  if (newest === undefined || oldest === undefined) return { resolvedYear: targetYear, isExact: false }; // unreachable: length > 0
  const resolvedYear = targetYear > newest ? newest : oldest;
  return { resolvedYear, isExact: false };
}

/**
 * Get federal tax configuration for a specific year.
 * Falls back to the nearest registered year if the requested year isn't
 * available (see resolveTaxYear).
 */
export function getFederalTaxConfig(year?: number): FederalTaxConfig {
  const { resolvedYear } = resolveTaxYear(year);
  // resolveTaxYear only returns a registered year while the registry is
  // non-empty, and it is populated statically at module load.
  const config = TAX_YEARS_REGISTRY[resolvedYear];
  assertDefined(config, `No tax configuration registered for ${resolvedYear}`);
  return config;
}

/**
 * Get federal income tax brackets for a specific year and filing status
 */
export function getFederalBrackets(
  filingStatus: FilingStatus,
  year?: number
): TaxBracket[] {
  const config = getFederalTaxConfig(year);
  return config.brackets[filingStatus] || config.brackets.single;
}

/**
 * Get capital gains tax brackets for a specific year and filing status
 */
export function getCapitalGainsBrackets(
  filingStatus: FilingStatus,
  year?: number
): TaxBracket[] {
  const config = getFederalTaxConfig(year);
  return config.capitalGainsBrackets[filingStatus] || config.capitalGainsBrackets.single;
}

/**
 * Get standard deduction for a specific year and filing status
 */
export function getStandardDeduction(
  filingStatus: FilingStatus | string,
  year?: number
): number {
  // An unknown status fails loudly instead of silently using another
  // status's (or an invented) deduction (TAX-26).
  const status = requireFilingStatus(filingStatus);
  return getFederalTaxConfig(year).standardDeductions[status];
}

const FILING_STATUS_ALIASES: Readonly<Record<string, FilingStatus>> = {
  single: FilingStatus.Single,
  s: FilingStatus.Single,
  marriedfilingjointly: FilingStatus.MarriedFilingJointly,
  mfj: FilingStatus.MarriedFilingJointly,
  married: FilingStatus.MarriedFilingJointly,
  marriedfilingseparately: FilingStatus.MarriedFilingSeparately,
  mfs: FilingStatus.MarriedFilingSeparately,
  headofhousehold: FilingStatus.HeadOfHousehold,
  hoh: FilingStatus.HeadOfHousehold,
};

/**
 * The one filing-status normalizer (TAX-25): snake_case, camelCase, spaced
 * or abbreviated (mfj/mfs/hoh) spellings, any case → `FilingStatus`, or null
 * when unrecognized.
 */
export function normalizeFilingStatus(value: string | null | undefined): FilingStatus | null {
  if (!value) return null;
  const key = value.replace(/[\s_-]/g, '').toLowerCase();
  return FILING_STATUS_ALIASES[key] ?? null;
}

/** `normalizeFilingStatus` that throws on an unrecognized value. */
export function requireFilingStatus(value: string | null | undefined): FilingStatus {
  const status = normalizeFilingStatus(value);
  if (!status) throw new RangeError(`Unknown filing status: ${String(value)}`);
  return status;
}

/**
 * Get NIIT threshold for a specific year and filing status
 */
export function getNiitThreshold(
  filingStatus: FilingStatus,
  year?: number
): number {
  const config = getFederalTaxConfig(year);
  return config.niitThresholds[filingStatus] ?? config.niitThresholds.single;
}

/**
 * Get NIIT rate for a specific year
 */
export function getNiitRate(year?: number): number {
  return getFederalTaxConfig(year).niitRate;
}

/**
 * Get Social Security wage base for a specific year
 */
export function getSocialSecurityWageBase(year?: number): number {
  return getFederalTaxConfig(year).socialSecurityWageBase;
}

/**
 * Get Medicare additional rate threshold for a specific filing status
 */
export function getAdditionalMedicareThreshold(
  filingStatus: FilingStatus,
  year?: number
): number {
  const config = getFederalTaxConfig(year);
  return config.additionalMedicareThresholds[filingStatus] ?? 
    config.additionalMedicareThresholds.single;
}

// ============================================================================
// Re-exports for convenience
// ============================================================================

export {
  // State utilities
  STATE_CODES,
  NO_INCOME_TAX_STATES,
  getStateName,
  getStateCode,
  getStateTaxConfig,
  getStateTaxRate,
  hasStateIncomeTax,
  getStateStandardDeduction,
  getStateTaxConfigsForYear,
  getAvailableStateTaxYears,
  states2024,
  states2025,
  states2026,

  // Year configs for direct access if needed
  federal2024,
  federal2025,
  federal2026
};

// Exact state income-tax models (TAX-18)
export * from './stateModels/index.js';
